import { Metadata } from "next";

/**
 * 站点基础 URL（确保不带尾部斜杠）
 */
const rawSiteUrl = process.env.MORPH_SITE_URL || "https://morph.network";
export const SITE_BASE_URL = rawSiteUrl.endsWith("/")
  ? rawSiteUrl.slice(0, -1)
  : rawSiteUrl;

/**
 * 站点名称
 */
export const SITE_NAME = "Morph";

/**
 * 默认分享图片路径（用于 OG 和 Twitter Card）
 * 推荐尺寸：1200x630 像素
 */
export const DEFAULT_OG_IMAGE = "/share/share-morph.jpeg";

/**
 * 生成规范化的 canonical URL
 * - 移除查询参数
 * - 移除尾部斜杠（除了根路径）
 * - 确保使用完整的绝对 URL
 *
 * @param path - 页面路径，如 '/apps', '/about'
 * @returns 完整的 canonical URL
 */
export function getCanonicalUrl(path: string): string {
  // 规范化路径：移除尾部斜杠（除了根路径）
  let normalizedPath = path;

  // 移除查询参数
  const queryIndex = normalizedPath.indexOf("?");
  if (queryIndex !== -1) {
    normalizedPath = normalizedPath.substring(0, queryIndex);
  }

  // 移除 hash
  const hashIndex = normalizedPath.indexOf("#");
  if (hashIndex !== -1) {
    normalizedPath = normalizedPath.substring(0, hashIndex);
  }

  // 确保路径以 / 开头
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = "/" + normalizedPath;
  }

  // 移除尾部斜杠（除了根路径 /）
  if (normalizedPath.length > 1 && normalizedPath.endsWith("/")) {
    normalizedPath = normalizedPath.slice(0, -1);
  }

  // 根路径返回不带尾部斜杠的 URL
  if (normalizedPath === "/") {
    return SITE_BASE_URL;
  }

  return `${SITE_BASE_URL}${normalizedPath}`;
}

/**
 * 获取绝对路径的图片 URL
 * @param imagePath - 图片路径，可以是相对路径或绝对路径
 * @returns 完整的图片 URL
 */
export function getAbsoluteImageUrl(imagePath: string): string {
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }
  // 确保路径以 / 开头
  const normalizedPath = imagePath.startsWith("/")
    ? imagePath
    : "/" + imagePath;
  return `${SITE_BASE_URL}${normalizedPath}`;
}

/**
 * 创建包含 canonical 和 hreflang 的 metadata 配置
 *
 * @param path - 页面路径
 * @param metadata - 其他 metadata 配置
 * @returns 完整的 Metadata 对象
 */
export function createMetadata(
  path: string,
  metadata: Partial<Metadata> = {},
): Metadata {
  const canonicalUrl = getCanonicalUrl(path);

  return {
    ...metadata,
    alternates: {
      ...metadata.alternates,
      canonical: canonicalUrl,
      languages: {
        en: canonicalUrl,
        "x-default": canonicalUrl,
      },
    },
  };
}

/**
 * 基础 metadata 配置
 */
export const baseMetadata: Metadata = {
  title: "Morph Network | Onchain Settlement Layer for Global Crypto Payments",
  description:
    "Morph is a secure settlement layer for global crypto payments, offering seamless on-chain stablecoin infrastructure with BGB",
  keywords:
    "crypto payment gateway, Morph, scaling solution, Morph mainnet, Morph testnet, Morph roadmap, optimistic zk-rollup, validity proofs",
};

/**
 * 创建带有完整 OG 和 Twitter Card 标签的 metadata
 *
 * @param path - 页面路径
 * @param overrides - 覆盖的 metadata 配置
 * @returns 完整的 Metadata 对象，包含规范的 OG 和 Twitter Card 标签
 *
 * @example
 * ```typescript
 * // 基础用法
 * export const metadata = createPageMetadata('/about', {
 *   title: "About Morph",
 *   description: "Learn about Morph"
 * })
 *
 * // 自定义图片
 * export const metadata = createPageMetadata('/tokenomics', {
 *   title: "Tokenomics",
 *   description: "BGB Token Economics",
 *   openGraph: {
 *     images: '/custom-image.png'
 *   }
 * })
 * ```
 */
export function createPageMetadata(
  path: string,
  overrides: Partial<Metadata> = {},
): Metadata {
  const canonicalUrl = getCanonicalUrl(path);
  const title = overrides.title || baseMetadata.title;
  const description = overrides.description || baseMetadata.description;

  // 处理 OG 图片 - 确保使用绝对路径
  const ogImages = overrides.openGraph?.images || DEFAULT_OG_IMAGE;
  const ogImageUrl =
    typeof ogImages === "string"
      ? getAbsoluteImageUrl(ogImages)
      : Array.isArray(ogImages) && ogImages.length > 0
        ? typeof ogImages[0] === "string"
          ? getAbsoluteImageUrl(ogImages[0])
          : getAbsoluteImageUrl(
              (ogImages[0] as { url: string }).url || DEFAULT_OG_IMAGE,
            )
        : getAbsoluteImageUrl(DEFAULT_OG_IMAGE);

  // 处理 Twitter 图片 - 确保使用绝对路径
  const twitterImages = overrides.twitter?.images || ogImages;
  const twitterImageUrl =
    typeof twitterImages === "string"
      ? getAbsoluteImageUrl(twitterImages)
      : Array.isArray(twitterImages) && twitterImages.length > 0
        ? typeof twitterImages[0] === "string"
          ? getAbsoluteImageUrl(twitterImages[0])
          : getAbsoluteImageUrl(
              (twitterImages[0] as { url: string }).url || DEFAULT_OG_IMAGE,
            )
        : ogImageUrl;

  // 获取有效的 title 和 description 字符串
  const ogTitle =
    (overrides.openGraph?.title as string) ||
    (title as string) ||
    (baseMetadata.title as string);
  const ogDescription =
    (overrides.openGraph?.description as string) ||
    (description as string) ||
    (baseMetadata.description as string);
  const twitterTitle = (overrides.twitter?.title as string) || ogTitle;
  const twitterDescription =
    (overrides.twitter?.description as string) || ogDescription;

  return {
    ...baseMetadata,
    ...overrides,
    alternates: {
      ...overrides.alternates,
      canonical: canonicalUrl,
      languages: {
        en: canonicalUrl,
        "x-default": canonicalUrl,
      },
    },
    // Open Graph 标签 - 完整配置
    openGraph: {
      type: "website" as const,
      siteName: SITE_NAME,
      title: ogTitle,
      description: ogDescription,
      url: canonicalUrl,
      images: [
        {
          url: ogImageUrl,
          width: 1200,
          height: 630,
          alt: typeof title === "string" ? title : SITE_NAME,
        },
      ],
      locale: "en_US",
    },
    // Twitter Card 标签 - 完整配置
    twitter: {
      card: "summary_large_image" as const,
      title: twitterTitle,
      description: twitterDescription,
      images: [twitterImageUrl],
      site: "@MorphNetwork",
      creator: "@MorphNetwork",
    },
  };
}

/**
 * 根布局的 metadata 配置
 * 用于 app/layout.tsx
 */
export const rootMetadata: Metadata = {
  // metadataBase: new URL(SITE_BASE_URL),
  title: {
    default:
      "Morph Network | Onchain Settlement Layer for Global Crypto Payments",
    template: "%s | Morph",
  },
  description:
    "Morph is a secure settlement layer for global crypto payments, offering seamless on-chain stablecoin infrastructure with BGB",
  keywords:
    "crypto payment gateway, Morph, scaling solution, Morph mainnet, Morph testnet, Morph roadmap, optimistic zk-rollup, validity proofs",
  authors: [{ name: "Morph" }],
  creator: "Morph",
  publisher: "Morph",
  formatDetection: {
    email: false,
    address: false,
    telephone: false,
  },
  // canonical 和 hreflang 标签配置
  alternates: {
    canonical: SITE_BASE_URL,
    languages: {
      en: SITE_BASE_URL,
      "x-default": SITE_BASE_URL,
    },
  },
  icons: {
    icon: [
      { url: "/favicon.ico", sizes: "16x16", type: "image/x-icon" },
      {
        url: "/assets/favicon/favicon-48x48.png",
        sizes: "48x48",
        type: "image/png",
      },
      {
        url: "/assets/favicon/favicon-32x32.png",
        sizes: "32x32",
        type: "image/png",
      },
      {
        url: "/assets/favicon/favicon-16x16.png",
        sizes: "16x16",
        type: "image/png",
      },
      {
        url: "/assets/favicon/android-chrome-192x192.png",
        sizes: "192x192",
        type: "image/png",
      },
    ],
    apple: [
      { url: "/assets/favicon/apple-touch-icon-180x180.png", sizes: "180x180" },
    ],
  },
  manifest: "/site.webmanifest",
  // Open Graph 默认配置
  openGraph: {
    type: "website",
    siteName: SITE_NAME,
    title:
      "Morph Network | Onchain Settlement Layer for Global Crypto Payments",
    description:
      "Morph is a secure settlement layer for global crypto payments, offering seamless on-chain stablecoin infrastructure with BGB",
    url: SITE_BASE_URL,
    images: [
      {
        url: getAbsoluteImageUrl(DEFAULT_OG_IMAGE),
        width: 1200,
        height: 630,
        alt: "Morph - Onchain Settlement Layer",
      },
    ],
    locale: "en_US",
  },
  // Twitter Card 默认配置
  twitter: {
    card: "summary_large_image",
    title:
      "Morph Network | Onchain Settlement Layer for Global Crypto Payments",
    description:
      "Morph is a secure settlement layer for global crypto payments, offering seamless on-chain stablecoin infrastructure with BGB",
    images: [getAbsoluteImageUrl(DEFAULT_OG_IMAGE)],
    site: "@MorphNetwork",
    creator: "@MorphNetwork",
  },
};
